import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    ITextSnippetDialogData,
    OnCreateTextSnippetFailed,
    OnCreateTextSnippetSuccessfully,
    OnUpdateTextSnippetFailed,
    OnUpdateTextSnippetSuccessfully,
    TextSnippetComponent,
    TextSnippetProvider,
} from '@modeso/aumico-lib-annualreport-fe';
import { AumicoCoreProvider, MAT_DIALOG_CUSTOM_CLASS, NotificationType } from '@modeso/aumico-lib-core-fe';
import { ITextSnippetDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { BasePageComponent } from '../../../../pages/base.page';
import { localizationKeys } from '../../../../utils/localizationKeys';

const CREATE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_create_success_notification_description;
const UPDATE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_update_success_notification_description;
const SUCCESS_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_success_notification_title;
const ERROR_TITLE_LOCALIZATION_KEY =
    localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_error_notification_title;
const UPDATE_DIALOG_TITLE = localizationKeys.aumico_reports_organization_settings_text_snippets_update_dialog_title;

@UntilDestroy()
@Component({
    selector: 'app-page-text-snippets.page',
    templateUrl: './text-snippets.page.html',
    styleUrls: ['./text-snippets.page.scss'],
})
export class TextSnippetsPageComponent extends BasePageComponent implements OnInit {
    private editMode = false;
    private dialogRef: MatDialogRef<TextSnippetComponent, void> | undefined;
    private descriptionExistsError = new Subject<boolean>();

    constructor(
        injector: Injector,
        dialog: MatDialog,
        private readonly textSnippetProvider: TextSnippetProvider,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        super(injector, dialog);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.disableProceedButton = false;
        this.textSnippetProvider
            .getSelectedTextSnippetById$()
            .pipe(
                filter((selectedTextSnippet) => selectedTextSnippet != null),
                untilDestroyed(this),
            )
            .subscribe((selectedTextSnippet: ITextSnippetDTO) => {
                this.editMode = true;
                this.openTextSnippetDialog(selectedTextSnippet);
            });

        this.actionsListener$.pipe(ofType(OnCreateTextSnippetSuccessfully), untilDestroyed(this)).subscribe(() => {
            this.onFireSuccessNotification();
            this.dialogRef?.close();
            this.textSnippetProvider.dispatchTextSnippetsList();
        });

        this.actionsListener$.pipe(ofType(OnCreateTextSnippetFailed), untilDestroyed(this)).subscribe((error) => {
            this.handleCreateTextSnippetFailed(error.payload);
        });

        this.actionsListener$.pipe(ofType(OnUpdateTextSnippetSuccessfully), untilDestroyed(this)).subscribe(() => {
            this.dialogRef?.close();
            this.onFireSuccessNotification();
            this.textSnippetProvider.dispatchTextSnippetsList();
        });

        this.actionsListener$.pipe(ofType(OnUpdateTextSnippetFailed), untilDestroyed(this)).subscribe((error) => {
            this.fireErrorNotification(error.payload);
        });
    }

    public openTextSnippetDialog(selectedTextSnippet?: ITextSnippetDTO): void {
        const dialogRef = this.dialog?.open<TextSnippetComponent, ITextSnippetDialogData, void>(TextSnippetComponent, {
            width: '70%',
            disableClose: true,
            data: {
                titleLocalizationKey: UPDATE_DIALOG_TITLE,
                textSnippetData: selectedTextSnippet,
                descriptionExistsError: this.descriptionExistsError,
            },
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });

        this.dialogRef = dialogRef;

        dialogRef?.componentInstance.saveButtonPressed.pipe(untilDestroyed(this)).subscribe((textSnippet) => {
            if (this.editMode) {
                this.textSnippetProvider.dispatchUpdateTextSnippet(textSnippet);
            } else {
                this.textSnippetProvider.createTextSnippet(textSnippet);
            }
        });

        dialogRef
            ?.afterClosed()
            .pipe(first(), untilDestroyed(this))
            .subscribe(() => {
                this.editMode = false;
                this.textSnippetProvider.resetSelectedTextSnippet();
            });
    }

    private onFireSuccessNotification(): void {
        let successNotificationDescriptionKey: string = CREATE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY;
        if (this.editMode) {
            successNotificationDescriptionKey = UPDATE_SUCCESS_DESCRIPTION_LOCALIZATION_KEY;
        }
        this.fireSuccessNotification({
            titleKey: SUCCESS_TITLE_LOCALIZATION_KEY,
            descriptionKey: successNotificationDescriptionKey,
        });
    }

    private handleCreateTextSnippetFailed(error: HttpErrorResponse): void {
        if (error.status === HttpStatusCode.Conflict) {
            this.descriptionExistsError.next(true);
        }
        this.fireErrorNotification(error);
    }

    private fireErrorNotification(error: HttpErrorResponse): void {
        const errorDescriptionLocalizationKey = this.getErrorNotificationLocalizationKey(error);

        this.coreProvider.dispatchAddNotification(
            ERROR_TITLE_LOCALIZATION_KEY,
            errorDescriptionLocalizationKey,
            NotificationType.ERROR,
        );
    }

    private getErrorNotificationLocalizationKey(error: HttpErrorResponse): string {
        if (this.editMode) {
            return localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_update_error_notification_description;
        }

        if (error.status === HttpStatusCode.Conflict) {
            return localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_create_conflict_error_notification_description;
        }

        return localizationKeys.aumico_reports_organization_settings_text_snippets_form_dialog_create_internal_server_error_description;
    }
}
